import React, { memo, useEffect, useMemo, useState, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import QuickChargeModal from "@/components/billing/QuickChargeModal";
import AddStudentDialog from "@/components/students/AddStudentDialog";
import HivePadModal from "@/components/hivepad/HivePadModal";
import { 
  Users, 
  DollarSign, 
  GraduationCap, 
  TrendingUp, 
  Calendar,
  Activity,
  BarChart3,
  PieChart,
  Clock,
  UserCog,
  X,
  ArrowRight,
  StickyNote
} from "lucide-react";

// Layout components
import {
  MaterialDashboardGrid,
  MetricCardsGrid,
  ChartsGrid,
  MaterialSection,
  MaterialCard,
} from "@/components/dashboard-material/layout/MaterialDashboardGrid";

// Metric components
import {
  StudentMetricCard,
  RevenueMetricCard,
  AttendanceMetricCard,
  GrowthMetricCard,
} from "@/components/dashboard-material/metrics/MaterialMetricCard";

// Hooks
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useAuth } from "@/hooks/useAuth";
import { useOnlineStatus } from "@/utils/connectionUtils";
import { useQuery } from "@tanstack/react-query";

// Utilities
import { materialTheme } from "@/lib/material-design-tokens";
import ConnectionErrorAlert from "@/components/common/ConnectionErrorAlert";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useNavigate } from "react-router-dom";
import { SubscriptionService } from "@/services/subscriptionService";
import { FeedbackCard } from "@/components/dashboard/FeedbackCard";
import { useFeedbackCardDismiss } from "@/hooks/useFeedbackCardDismiss";
import { Button } from "@/components/ui/button";

// Chart components
import { RevenueChart } from "@/components/reporting/RevenueChart";
import { BirthdayCard } from "@/components/dashboard/BirthdayCard";
import { useBirthdays } from "@/hooks/useBirthdays";

// Analytics Intelligence Cards
import { AtRiskStudentsCard } from "@/components/analytics";
import { PaymentHealthCard } from "@/components/analytics/PaymentHealthCard";
import { VipFamilyCard } from "@/components/analytics/VipFamilyCard";
import { MessageResponseCard } from "@/components/analytics/MessageResponseCard";
import { ClassPerformanceCard } from "@/components/analytics/ClassPerformanceCard";
import { ActiveStudentsNotEnrolledCard } from "@/components/analytics/ActiveStudentsNotEnrolledCard";

// AI Component
const AskHivetivityInterface = React.lazy(() => import('@/components/ai/AskHivetivityInterface').then(m => ({ default: m.default })));


// Animation variants for the page
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: materialTheme.motion.curves.decelerate,
    },
  },
};


// Temporary chart placeholders (will be replaced with actual charts)
const ChartPlaceholder: React.FC<{ 
  title: string; 
  icon: React.ReactNode; 
  color: string;
  height?: string;
}> = ({ title, icon, color, height = "300px" }) => (
  <div 
    className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
    style={{ height }}
  >
    <div className="flex items-center space-x-3 mb-4">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: color + '20', color }}
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div 
      className="flex items-center justify-center rounded-lg border-2 border-dashed"
      style={{ 
        height: 'calc(100% - 60px)',
        borderColor: color + '40',
        backgroundColor: color + '05',
      }}
    >
      <div className="text-center">
        <div 
          className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
          style={{ backgroundColor: color + '20', color }}
        >
          {icon}
        </div>
        <p className="text-sm text-gray-500">Analytics will generate once Hivetivity knows more about your business. Add more data to see insights.</p>
      </div>
    </div>
  </div>
);


// Trial Status Card Component
const TrialStatusCard: React.FC<{
  daysLeft: number;
  onUpgradeClick: () => void;
}> = ({ daysLeft, onUpgradeClick }) => {
  const totalTrialDays = 14;
  const progressPercentage = Math.max(0, Math.min(100, ((totalTrialDays - daysLeft) / totalTrialDays) * 100));
  
  const getProgressColor = () => {
    if (daysLeft > 7) return 'from-green-500 to-green-600';
    if (daysLeft > 3) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-red-600';
  };

  return (
    <MaterialCard>
      <div className="relative">
        {/* Icon */}
        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mb-3">
          <Clock className="w-4 h-4 text-purple-600" />
        </div>

        {/* Content */}
        <div>
          <h3 className="text-xs font-medium text-gray-600 mb-2">
            Trial Period
          </h3>
          <div className="text-2xl font-bold text-purple-900 mb-4">
            {daysLeft} Days Left
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className={`h-2 rounded-full bg-gradient-to-r ${getProgressColor()} transition-all duration-500`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 text-center">
              {Math.round(progressPercentage)}% trial used
            </div>
          </div>

          {/* See Pricing Options Button */}
          <div className="mb-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => window.location.href = '/admin/subscription-center?view=pricing'}
            >
              See Pricing Options
            </Button>
          </div>

          {/* CTAs */}
          <div className="space-y-2">
            <button
              onClick={onUpgradeClick}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </MaterialCard>
  );
};

const MaterialDashboard: React.FC = () => {
  console.log("MaterialDashboard: render start");
  
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { metrics, isLoading: metricsLoading } = useDashboardData();
  const { data: birthdays = [], isLoading: birthdaysLoading } = useBirthdays();
  
  const isOnline = useOnlineStatus();
  const { user, userRole, isAdmin, organizationId } = useAuth();
  const navigate = useNavigate();
  const [showQuickCharge, setShowQuickCharge] = useState(false);
  const [showHivePadModal, setShowHivePadModal] = useState(false);
  const { isDismissed, dismissCard } = useFeedbackCardDismiss();

  // Handle business trial setup flow and ensure page starts at top
  useEffect(() => {
    // Multiple scroll attempts to ensure page starts at top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also set it after a brief delay in case content is still loading
    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('setup') === 'new') {
      // Show welcome message for new trial users
      toast.success("Welcome to Hivetivity!", {
        description: "🎉 Your 14-day free trial is now active! You have full access to all Essential Plan features. Start exploring your dashboard to set up your activity center.",
        duration: 8000,
      });
      
      // Clean up URL to remove setup parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    return () => clearTimeout(scrollTimer);
  }, []);

  // Fetch trial status using subscription service
  const { data: trialStatus, isLoading: trialLoading } = useQuery({
    queryKey: ['trialStatus', organizationId],
    queryFn: () => SubscriptionService.getOrganizationTrialStatus(organizationId!),
    enabled: !!organizationId,
    staleTime: 0, // Always consider data stale to ensure fresh data
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Fetch current subscription
  const { data: subscription } = useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: () => SubscriptionService.getCurrentSubscription(organizationId!),
    enabled: !!organizationId,
    staleTime: 0, // Always consider data stale to ensure fresh data
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Determine trial stage based on subscription data
  const trialStage = useMemo(() => {
    if (subscription?.status === 'active') return 'upgraded';
    if (!trialStatus?.isInTrial) return 'upgraded';
    if (trialStatus?.trialDaysRemaining <= 2) return 'sneak-peek';
    return 'trial';
  }, [subscription, trialStatus]);
  
  const isTrialUser = trialStage === 'trial' || trialStage === 'sneak-peek';
  const daysLeft = trialStatus?.trialDaysRemaining || 14;

  // Dashboard mode state - manual override or auto-detect
  const [dashboardMode, setDashboardMode] = useState<'simple' | 'analytics'>(() => {
    // Check localStorage for user preference
    const saved = localStorage.getItem('hivetivity-dashboard-mode');
    if (saved) return saved as 'simple' | 'analytics';
    
    // Default to simple mode on first load
    return 'simple';
  });

  // Handle manual dashboard toggle
  const toggleDashboardMode = () => {
    const newMode = dashboardMode === 'simple' ? 'analytics' : 'simple';
    setDashboardMode(newMode);
    localStorage.setItem('hivetivity-dashboard-mode', newMode);
  };

  // Mock sparkline data for demonstration
  const mockSparklineData = [12, 19, 3, 17, 6, 3, 19, 15, 12, 6, 14, 18];

  // Calculate metric values with real database data
  const metricValues = useMemo(() => {
    const values = {
      activeStudents: stats?.students || 0, // Total students from organization
      activeClasses: stats?.classes || 0,   // Active classes only (filtered by status='active')
      staffMembers: stats?.staffMembers || 0, // Staff, bizadmin, and owner users in organization
      monthlyRevenue: metrics?.revenue?.currentMonth || 0,
      attendanceRate: metrics?.attendance?.rate || 0,
      growthRate: metrics?.revenue?.percentageChange || 0,
    };
    
    return values;
  }, [metrics, stats]);

  // Smart dashboard transition logic - MUST come after metricValues
  const hasEnoughDataForAnalytics = useMemo(() => {
    return (
      metricValues.activeStudents >= 5 && 
      metricValues.activeClasses >= 2 && 
      metricValues.monthlyRevenue > 0
    );
  }, [metricValues]);
  
  // Check if we have historical data for trends (based on organization age)
  const hasHistoricalData = useMemo(() => {
    // For now, we'll consider any revenue data as indication of some history
    // In the future, this could check organization creation date
    return metrics?.revenue?.previousMonth > 0 || metrics?.attendance?.previousRate > 0;
  }, [metrics]);
  
  // Debug logging for dashboard mode
  useEffect(() => {
    console.log("Dashboard Mode:", {
      dashboardMode,
      hasEnoughDataForAnalytics,
      toggleVisible: (dashboardMode === 'analytics' || hasEnoughDataForAnalytics)
    });
  }, [dashboardMode, hasEnoughDataForAnalytics]);

  // Auto-upgrade to analytics when criteria met (only if user hasn't manually chosen simple)
  useEffect(() => {
    const userChoice = localStorage.getItem('hivetivity-dashboard-mode');
    if (!userChoice && hasEnoughDataForAnalytics && dashboardMode === 'simple') {
      setDashboardMode('analytics');
      // Show celebration toast
      toast.success("🎉 Analytics Dashboard Unlocked!", {
        description: "You now have enough data to see advanced insights!",
        duration: 5000,
      });
    }
  }, [hasEnoughDataForAnalytics, dashboardMode]);

  // Dynamic welcome message based on progress
  const getDynamicWelcomeMessage = () => {
    const hasStudents = metricValues.activeStudents > 0;
    const hasRevenue = metricValues.monthlyRevenue > 0;
    const hasClasses = metricValues.activeClasses > 0;

    if (hasStudents && hasRevenue && hasClasses) {
      return "🚀 You're crushing it! Your program is in full swing.";
    } else if (hasStudents && hasClasses) {
      return "⭐ Great progress! You have students and classes running.";
    } else if (hasStudents || hasClasses) {
      return "🌱 Nice start! Let's get your program fully set up.";
    } else {
      return "✨ Ready to build something amazing? Let's set up your business essentials.";
    }
  };


  // Trial-specific handlers
  const handleSparkleClick = () => {}; // No-op function since we removed sparkle functionality
  
  const handleUpgradeClick = async () => {
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }

    try {
      // Create upgrade checkout session using the new upgrade-checkout function
      const result = await SubscriptionService.createUpgradeCheckoutSession(
        organizationId,
        "price_1RifjIL45zl3J0lpsFeupPtl", // Essential plan price ID
        undefined, // No existing customer ID needed
        `${window.location.origin}/admin?upgrade=success`,
        `${window.location.origin}/admin`
      );

      if (result?.url) {
        // Redirect to Stripe checkout
        window.location.href = result.url;
      } else {
        toast.error("Could not start upgrade process. Please try again.");
      }
    } catch (error) {
      console.error('Upgrade checkout error:', error);
      toast.error("Could not start upgrade process. Please try again.");
    }
  };

  // Format currency helper
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };


  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen"
      style={{ backgroundColor: materialTheme.colors.surface.background }}
    >
      <MaterialDashboardGrid>
        {/* Feedback Card - At the very top */}
        {!isDismissed && (
          <FeedbackCard onDismiss={dismissCard} />
        )}

        {/* Birthday Card */}
        <ErrorBoundary componentName="BirthdayCard">
          <BirthdayCard birthdays={birthdays} />
        </ErrorBoundary>

        {/* Offline Alert */}
        {!isOnline && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConnectionErrorAlert />
          </motion.div>
        )}

        {/* Welcome Section */}
        <MaterialSection
          title={
            <div className="flex items-center justify-between">
              <span>{isTrialUser ? "Welcome to Hivetivity!" : "Dashboard Overview"}</span>
              {/* Show toggle when either in analytics mode OR have enough data for analytics */}
              {(dashboardMode === 'analytics' || hasEnoughDataForAnalytics) && (
                <button
                  onClick={toggleDashboardMode}
                  className="flex items-center gap-2 px-4 py-2 ml-4 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  {dashboardMode === 'simple' ? (
                    <>
                      <BarChart3 className="w-4 h-4" />
                      {hasEnoughDataForAnalytics ? "Switch to Analytics" : "Preview Analytics"}
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4" />
                      Switch to Simple
                    </>
                  )}
                  {hasEnoughDataForAnalytics && dashboardMode === 'simple' && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                </button>
              )}
            </div>
          }
          subtitle={isTrialUser ? getDynamicWelcomeMessage() : "Welcome back! Here's what's happening with your business today."}
        >
          {/* Welcome to Hivetivity Cards - Always 3 basic cards + trial card if trial user */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <ErrorBoundary componentName="WelcomeStudentCard">
              <StudentMetricCard
                title="Active Students"
                value={metricValues.activeStudents}
                icon={<Users />}
                loading={statsLoading || metricsLoading}
                onClick={() => navigate('/admin/students')}
                isTrialMode={true}
                showSparkle={false}
                onSparkleClick={handleSparkleClick}
                showCta={metricValues.activeStudents === 0}
                ctaText="🌟 Add your first student to get started!"
                onCtaClick={() => navigate('/admin/students')}
                compact={true}
              />
            </ErrorBoundary>

            <ErrorBoundary componentName="WelcomeRevenueCard">
              <RevenueMetricCard
                title="Monthly Revenue"
                value={formatCurrency(metricValues.monthlyRevenue)}
                icon={<DollarSign />}
                loading={statsLoading || metricsLoading}
                onClick={() => navigate('/admin/billing')}
                isTrialMode={true}
                showSparkle={false}
                onSparkleClick={handleSparkleClick}
                showCta={metricValues.monthlyRevenue === 0}
                ctaText="💳 Ready to get paid? Set up billing!"
                onCtaClick={() => navigate('/admin/billing')}
                compact={true}
              />
            </ErrorBoundary>

            <ErrorBoundary componentName="WelcomeClassCard">
              <AttendanceMetricCard
                title="Active Classes"
                value={metricValues.activeClasses}
                icon={<Calendar />}
                loading={statsLoading || metricsLoading}
                onClick={() => navigate('/admin/classes')}
                isTrialMode={true}
                showSparkle={false}
                onSparkleClick={handleSparkleClick}
                showCta={metricValues.activeClasses === 0}
                ctaText="📅 Create your first class offering!"
                onCtaClick={() => navigate('/admin/classes')}
                compact={true}
              />
            </ErrorBoundary>

            {/* Trial Status Card - spans 2 columns, only shows for trial users */}
            {isTrialUser && (
              <div className="md:col-span-2 lg:col-span-2">
                <ErrorBoundary componentName="TrialStatusCard">
                  <TrialStatusCard
                    daysLeft={daysLeft}
                    onUpgradeClick={handleUpgradeClick}
                  />
                </ErrorBoundary>
              </div>
            )}
          </div>
        </MaterialSection>

        {/* Quick Actions Section */}
        <MaterialSection
          title="Quick Actions"
          subtitle="Daily tasks and frequent operations"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() => window.location.href = 'https://app.hivetivity.com/admin/schedule?view=today'}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Today's Classes</span>
              </div>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/classes?tab=attendance'}
              className="p-4 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-200 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <UserCog className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-purple-900">Take Attendance</span>
              </div>
            </button>

            <button
              onClick={() => setShowQuickCharge(true)}
              className="p-4 bg-green-50 hover:bg-green-100 rounded-xl border border-green-200 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Quick Charge</span>
              </div>
            </button>

            <button
              onClick={() => setShowHivePadModal(true)}
              className="p-4 bg-orange-50 hover:bg-orange-100 rounded-xl border border-orange-200 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <StickyNote className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-orange-900">Add to HivePad</span>
              </div>
            </button>
          </div>
        </MaterialSection>

        {/* Analytics Section */}
        <MaterialSection
          title="Analytics"
          subtitle={dashboardMode === 'analytics' ? "Business intelligence insights for proactive management" : "Visual insights into your business performance"}
        >
          {dashboardMode === 'analytics' ? (
            /* Intelligence Cards - 6 cards in 2 rows of 3 */
            <div className="space-y-6">
              {/* Row 1: Critical Threats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ErrorBoundary componentName="AtRiskStudentsCard">
                  <AtRiskStudentsCard />
                </ErrorBoundary>
                
                <ErrorBoundary componentName="PaymentHealthCard">
                  <PaymentHealthCard />
                </ErrorBoundary>
                
                <ErrorBoundary componentName="VipFamilyCard">
                  <VipFamilyCard />
                </ErrorBoundary>
              </div>
              
              {/* Row 2: Growth Opportunities */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ErrorBoundary componentName="MessageResponseCard">
                  <MessageResponseCard />
                </ErrorBoundary>
                
                <ErrorBoundary componentName="ClassPerformanceCard">
                  <ClassPerformanceCard />
                </ErrorBoundary>
                
                <ErrorBoundary componentName="ActiveStudentsNotEnrolledCard">
                  <ActiveStudentsNotEnrolledCard />
                </ErrorBoundary>
              </div>
            </div>
          ) : (
            /* Charts - Simple mode */
            <ChartsGrid>
              <ErrorBoundary componentName="RevenueChart">
                <ChartPlaceholder
                  title="Revenue Trends"
                  icon={<DollarSign className="w-5 h-5" />}
                  color={materialTheme.colors.success[600]}
                />
              </ErrorBoundary>

              <ErrorBoundary componentName="StudentGrowthChart">
                <ChartPlaceholder
                  title="Student Growth"
                  icon={<TrendingUp className="w-5 h-5" />}
                  color={materialTheme.colors.primary[600]}
                />
              </ErrorBoundary>

              <ErrorBoundary componentName="PerformanceChart">
                <ChartPlaceholder
                  title="Class Performance"
                  icon={<PieChart className="w-5 h-5" />}
                  color={materialTheme.colors.warning[600]}
                />
              </ErrorBoundary>
            </ChartsGrid>
          )}
        </MaterialSection>

        {/* AI Assistant Section */}
        <MaterialSection
          title={
            <div className="flex items-center gap-2">
              <span>Ask Hivetivity AI</span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                BETA
              </span>
            </div>
          }
          subtitle="Get instant insights from your business data using natural language"
        >
          <ErrorBoundary componentName="AskHivetivityInterface">
            <Suspense fallback={<div>Loading AI assistant...</div>}>
              <AskHivetivityInterface />
            </Suspense>
          </ErrorBoundary>
        </MaterialSection>

      </MaterialDashboardGrid>


      {/* Quick Action Modals */}
      <QuickChargeModal
        open={showQuickCharge}
        onOpenChange={setShowQuickCharge}
      />
      
      <HivePadModal
        open={showHivePadModal}
        onOpenChange={setShowHivePadModal}
      />
      
    </motion.div>
  );
};

export default MaterialDashboard;